import { until } from '@vueuse/core'
import type { FilterOptions, Order } from '~/types'

interface FrequentOrderItem {
  name: string
  handle: string
  supplierHandle: string
  lastOrderedQuantity: number
  totalOrderQuantity: number
  lastOrderedReference?: string
  loading?: boolean
}

interface OrderHistoryResponse {
  totalOrders: number
  orders: Order[]
  filterOptions: FilterOptions
}

export type OrderRequestType = 'invoice' | 'orderStatus'

export const useOrderHistoryStore = defineStore('order-history', () => {
  const { baseURL } = useRuntimeConfig().app
  const { $i18n, $storeErrorLog } = useNuxtApp()
  const userStore = useUserStore()
  const accessToken = computed(() => userStore.accessToken)
  const headerFields = computed(() => ({ Authorization: `Bearer ${unref(accessToken)}` }))
  const abortIfNotAuthenticated = useAbortIfNotAuthenticated()
  const customerHandle = computed(() => userStore?.customerHandle ?? undefined)

  const itemsPerPage = computed(() => 10)
  const page = ref(1)
  const list = ref<Order[]>([])
  const totalOrders = ref(0)
  // Sticky "user has at least one order" flag, decoupled from `totalOrders`.
  // `totalOrders` is the count for the *current filter* and gets zeroed by
  // filter toggles, $reset (called e.g. after submitOrder in cart.ts), and
  // by reloads — which makes it unsuitable for menu visibility checks where
  // it would flicker. `hasAnyOrders` only flips false→true and is intentionally
  // not cleared by `$reset`.
  const hasAnyOrders = ref(false)
  const filterOptions = ref<FilterOptions>({})
  const filter = ref<Record<string, string[]>>({})
  const query = computed(() => ({
    supplier: filter.value.suppliers ?? [],
    page: page.value,
  }))

  const {
    data: ordersData,
    pending: isLoading,
    refresh: reloadOrders,
  } = useFetch<OrderHistoryResponse>(
    () => `/api/intern/customers/${unref(customerHandle)}/order-history`,
    {
      baseURL,
      headers: headerFields,
      query,
      watch: [accessToken],
      ...abortIfNotAuthenticated,
      onResponse: ({ response }) => {
        if (!response.ok) return
        // Dedup by handle: protects against offset-pagination boundary overlaps
        // (a new order shifting page boundaries) and against any stale fetch
        // landing on top of a reset list after a filter change.
        const seen = new Set(list.value.map(o => o.handle))
        const incoming: Order[] = (response._data.orders ?? []).filter((o: Order) => !seen.has(o.handle))
        list.value.push(...incoming)
        if (!totalOrders.value)
          totalOrders.value = response._data.totalOrders
        // Sticky flag: any non-zero response confirms the user has orders.
        // We never flip it back to false here — that's reserved for explicit
        // session changes (e.g. user logout/login).
        if (response._data.totalOrders > 0)
          hasAnyOrders.value = true
        if (!Object.keys(filterOptions.value).length)
          filterOptions.value = response._data.filterOptions
      },
      onResponseError({ response }) {
        // Fire-and-forget: refreshing the token flips `accessToken`, and the
        // `watch: [accessToken]` above performs the single recovery refetch.
        if (response.status === 401) void userStore.reloadAuth()
      },
    },
  )

  const totalPages = computed(() => Math.ceil(totalOrders.value / itemsPerPage.value))

  const {
    data: frequentOrdersData,
    refresh: reloadFrequentOrders,
  } = useFetch<FrequentOrderItem[]>(
    () => `/api/intern/customers/${unref(customerHandle)}/last-ordered-products`,
    {
      headers: headerFields,
      baseURL,
      watch: [accessToken],
      ...abortIfNotAuthenticated,
      onResponseError({ response }) {
        // Fire-and-forget: refreshing the token flips `accessToken`, and the
        // `watch: [accessToken]` above performs the single recovery refetch.
        if (response.status === 401) void userStore.reloadAuth()
      },
    },
  )

  const $reset = async () => {
    list.value = []
    filter.value = {}
    page.value = 1
    totalOrders.value = 0
    filterOptions.value = {}
    frequentOrdersData.value = null
    ordersData.value = null
  }

  const nextPage = () => page.value++

  const startPage = () => page.value = 1

  // Awaitable page advance: mutates `page`, then waits for the reactive
  // `useFetch` watcher to start and finish the resulting request. Replaces
  // the polling-based `waitUntilLoaded` in the page component, which could
  // resolve in the gap between `page.value++` and the watcher firing and
  // therefore allow a second `nextPage()` to land before the first fetch.
  const fetchNextPage = async () => {
    page.value++
    await nextTick()
    await until(isLoading).toBe(true, { timeout: 200, throwOnTimeout: false })
    await until(isLoading).toBe(false)
  }

  const toggleFilterValue = (key: string, value: string) => {
    list.value = []
    page.value = 1
    totalOrders.value = 0
    const active = filter.value[key]?.includes(value)
    if (active)
      filter.value[key] = filter.value[key].filter(v => v !== value)
    else
      filter.value[key] = [...(filter.value[key] ?? []), value]
  }

  const clearFilters = (...keys: string[]) => {
    list.value = []
    page.value = 1
    totalOrders.value = 0
    if (keys.length)
      keys.forEach(key => filter.value[key] = [])
    else
      filter.value = {}
  }

  const postOrderRequest = async (
    requestType: OrderRequestType,
    handle: string,
  ) => {
    if (!userStore.accessToken) return
    const urls: Record<OrderRequestType, string> = {
      invoice: `/api/v2/orders/${handle}/request-invoice`,
      orderStatus: `/api/v2/orders/${handle}/request-order-status`,
    }
    const [err] = await attempt($fetch(urls[requestType] as string, {
      method: 'POST',
      baseURL,
      onResponseError({ response }) {
        if (response.status === 401) void userStore.reloadAuth()
      },
    }))
    if (err) {
      $storeErrorLog(
        'stores/order-history.ts',
        `Could not post request for ${requestType}`,
        $i18n.t('errors.page.unknown.subtitle'),
        err,
      )
    }
  }

  return {
    $reset,
    itemsPerPage,
    page,
    ordersData,
    frequentOrdersData,
    filter,
    list,
    isLoading,
    totalOrders,
    totalPages,
    filterOptions,
    reloadOrders,
    reloadFrequentOrders,
    nextPage,
    fetchNextPage,
    startPage,
    toggleFilterValue,
    clearFilters,
    postOrderRequest,
    hasAnyOrders,
  }
})
